<template>
  <div class="setup-step">
    <h3>{{ sprintf(s__('ProjectSetup|Step %{step}: Silent Hours'), { step: stepNumber }) }}</h3>
    <p class="step-description">
      {{
        s__(
          'ProjectSetup|Set quiet hours when group chat notifications should not be sent. This helps maintain work-life balance for all project members.'
        )
      }}
    </p>

    <div v-if="!hasTimezone" class="warning-box">
      <strong>{{ s__('ProjectSetup|Timezone required') }}</strong>
      <p>
        {{
          s__(
            'ProjectSetup|Please set your preferred timezone in the previous step to configure silent hours.'
          )
        }}
      </p>
      <p v-if="goToStep" style="margin-top: 8px; margin-bottom: 0">
        <a href="#" @click.prevent="goToStep(1)" class="timezone-link">
          {{ s__('ProjectSetup|Go to Language & Timezone step') }}
        </a>
      </p>
    </div>

    <div v-else class="form-row">
      <label>{{ s__('ProjectSetup|Silent hours') }}</label>
      <div class="time-inputs">
        <div class="time-group">
          <label class="time-label">{{ s__('ProjectSetup|Start') }}</label>
          <div class="time-selectors">
            <select v-model="startHour" :disabled="saving || !hasTimezone" class="time-select">
              <option :value="null">--</option>
              <option v-for="h in 24" :key="h - 1" :value="String(h - 1).padStart(2, '0')">
                {{ String(h - 1).padStart(2, '0') }}
              </option>
            </select>
            <span>:</span>
            <select v-model="startMinute" :disabled="saving || !hasTimezone" class="time-select">
              <option :value="null">--</option>
              <option v-for="m in [0, 15, 30, 45]" :key="m" :value="String(m).padStart(2, '0')">
                {{ String(m).padStart(2, '0') }}
              </option>
            </select>
          </div>
        </div>
        <div class="time-group">
          <label class="time-label">{{ s__('ProjectSetup|End') }}</label>
          <div class="time-selectors">
            <select v-model="endHour" :disabled="saving || !hasTimezone" class="time-select">
              <option :value="null">--</option>
              <option v-for="h in 24" :key="h - 1" :value="String(h - 1).padStart(2, '0')">
                {{ String(h - 1).padStart(2, '0') }}
              </option>
            </select>
            <span>:</span>
            <select v-model="endMinute" :disabled="saving || !hasTimezone" class="time-select">
              <option :value="null">--</option>
              <option v-for="m in [0, 15, 30, 45]" :key="m" :value="String(m).padStart(2, '0')">
                {{ String(m).padStart(2, '0') }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="help-text">
        {{
          s__(
            'ProjectSetup|Do not send group chat notifications during these hours (times are in your preferred timezone)'
          )
        }}
      </div>
    </div>

    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">
      {{ s__('ProjectSetup|Preferences saved successfully!') }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, sprintf } from '@/locale'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  stepNumber: {
    type: Number,
    required: true,
  },
  timezones: {
    type: Array,
    default: () => [],
  },
  detectedTimezone: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
  goToStep: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['save', 'skip'])

const startHour = ref(null)
const startMinute = ref(null)
const endHour = ref(null)
const endMinute = ref(null)
const originalStartHour = ref(null)
const originalStartMinute = ref(null)
const originalEndHour = ref(null)
const originalEndMinute = ref(null)
const saveError = ref('')
const saveSuccess = ref(false)

const hasTimezone = computed(() => {
  return !!props.project?.preferredTimezone
})

const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $silentHours: JSON) {
    updateProject(id: $id, silentHours: $silentHours) {
      project {
        id
        silentHours
      }
      errors
    }
  }
`

const { mutate: updateProject } = useMutation(UPDATE_PROJECT_MUTATION)

// Convert silent hours from UTC to project's timezone for display
function convertSilentHoursFromUtc(silentHours, projectTimezone) {
  if (!silentHours || !silentHours.start || !silentHours.end) {
    return { start: null, end: null }
  }

  const tz = projectTimezone || 'UTC'

  // Parse UTC times and convert to project's timezone
  const startUtc = parseTimeString(silentHours.start)
  const endUtc = parseTimeString(silentHours.end)

  // Create Date objects for today with UTC times, then convert to project's timezone
  const today = new Date()
  const startDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      startUtc.hour,
      startUtc.minute
    )
  )
  const endDate = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      endUtc.hour,
      endUtc.minute
    )
  )

  // Format in project's timezone
  const startInTz = formatTimeInTimezone(startDate, tz)
  const endInTz = formatTimeInTimezone(endDate, tz)

  return { start: startInTz, end: endInTz }
}

function parseTimeString(timeStr) {
  const parts = timeStr.split(':')
  return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1] || '0', 10) }
}

function formatTimeInTimezone(date, timezone) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

// Initialize from project
watch(
  () => props.project,
  projectData => {
    if (projectData && projectData.silentHours) {
      // Convert silent hours from UTC to project's timezone for display
      const projectTz = projectData.preferredTimezone || 'UTC'
      const silentHoursInProjectTz = convertSilentHoursFromUtc(projectData.silentHours, projectTz)

      if (silentHoursInProjectTz.start) {
        const startParts = silentHoursInProjectTz.start.split(':')
        startHour.value = startParts[0] || null
        startMinute.value = startParts[1] || null
      } else {
        startHour.value = null
        startMinute.value = null
      }
      if (silentHoursInProjectTz.end) {
        const endParts = silentHoursInProjectTz.end.split(':')
        endHour.value = endParts[0] || null
        endMinute.value = endParts[1] || null
      } else {
        endHour.value = null
        endMinute.value = null
      }
      originalStartHour.value = startHour.value
      originalStartMinute.value = startMinute.value
      originalEndHour.value = endHour.value
      originalEndMinute.value = endMinute.value
    } else {
      startHour.value = null
      startMinute.value = null
      endHour.value = null
      endMinute.value = null
      originalStartHour.value = null
      originalStartMinute.value = null
      originalEndHour.value = null
      originalEndMinute.value = null
    }
  },
  { immediate: true }
)

// Auto-set minutes when hour is selected
watch(
  () => startHour.value,
  newHour => {
    if (newHour && !startMinute.value) {
      startMinute.value = '00'
    }
  }
)

watch(
  () => endHour.value,
  newHour => {
    if (newHour && !endMinute.value) {
      endMinute.value = '00'
    }
  }
)

// Check if there are unsaved changes
function hasChanges() {
  return (
    startHour.value !== originalStartHour.value ||
    startMinute.value !== originalStartMinute.value ||
    endHour.value !== originalEndHour.value ||
    endMinute.value !== originalEndMinute.value
  )
}

// Reset changes
function reset() {
  startHour.value = originalStartHour.value
  startMinute.value = originalStartMinute.value
  endHour.value = originalEndHour.value
  endMinute.value = originalEndMinute.value
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  reset,
  async save() {
    if (!props.project?.id) return

    saveError.value = ''
    saveSuccess.value = false

    try {
      const startTime =
        startHour.value && startMinute.value !== null
          ? `${startHour.value}:${startMinute.value}`
          : null
      const endTime =
        endHour.value && endMinute.value !== null ? `${endHour.value}:${endMinute.value}` : null

      const silentHours = startTime && endTime ? { start: startTime, end: endTime } : {}

      const { data } = await updateProject({
        id: props.project.id,
        silentHours: silentHours,
      })

      if (data?.updateProject?.errors?.length > 0) {
        throw new Error(data.updateProject.errors.join(', '))
      }

      saveSuccess.value = true
      originalStartHour.value = startHour.value
      originalStartMinute.value = startMinute.value
      originalEndHour.value = endHour.value
      originalEndMinute.value = endMinute.value
      emit('save', { silentHours })
      return true
    } catch (e) {
      saveError.value = e.message || 'Failed to save preferences'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.warning-box {
  padding: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  margin-bottom: 16px;
}

.warning-box strong {
  display: block;
  margin-bottom: 4px;
  color: #856404;
}

.warning-box p {
  margin: 0;
  font-size: 13px;
  color: #856404;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.time-inputs {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.time-group {
  flex: 1;
  min-width: 150px;
}

.time-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: normal;
}

.time-selectors {
  display: flex;
  gap: 8px;
  align-items: center;
}

.time-select {
  flex: 1;
  min-width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.time-select:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}

.timezone-link {
  color: #1d6bf3;
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
}

.timezone-link:hover {
  text-decoration: underline;
}
</style>
